import { ApiError, apiFetch, apiRequest } from './client';
/**
 * Diagnosis-tag API (Phase 7). This module pins the wire contract of
 * ReportTagsController exactly:
 *
 *   GET    /report-tags?scope=clinic:{id}|group:{id}&q=      -> { tags: ReportTagOption[] }
 *   POST   /report-tags {scope, label, aliases?, category?}  -> 201 ReportTagOption
 *                                                               (409 dedupe -> { id, message } with the existing id)
 *   PUT    /report-tags/{id} {label?, aliases?}              -> ReportTagOption (v1 rename+aliases; admin-gated)
 *   DELETE /report-tags/{id}                                 -> 204 archive (admin-gated)
 *   GET    /reports/{type}/{reportId}/tags                   -> { links: ReportTagLink[] } (chain union: root + addenda)
 *   PUT    /reports/{type}/{reportId}/tags {links: [...]}    -> { links: ReportTagLink[] } (replace-set for THIS report row)
 *
 * `{type}` is 'standard' | 'teleconsultation'. Link edits follow the same
 * authz ladder as report content edits and are frozen once the report is
 * final/amended (400); addendum flow: PUT targets the active addendum
 * draft's report id, while the GET unions links across the chain.
 */
const BASE = '/report-tags';
export function clinicTagScope(clinicId) {
    return `clinic:${clinicId}`;
}
export function groupTagScope(groupId) {
    return `group:${groupId}`;
}
/**
 * GET /report-tags/usage — tags in use across the caller's visible studies,
 * most-used first. Scope-free by design: the worklist spans every clinic the
 * user can view, so the counts do too.
 */
export async function getReportTagUsage(q, limit) {
    const params = new URLSearchParams();
    if (q)
        params.set('q', q);
    if (limit !== undefined)
        params.set('limit', String(limit));
    const qs = params.toString();
    const response = await apiFetch(`${BASE}/usage${qs ? `?${qs}` : ''}`);
    return response.tags;
}
/** Org tags matching `q` (active only), plus vocabulary suggestions flagged
 * source:'vocabulary' when a non-empty q isn't already covered by org tags. */
export async function getReportTags(scope, q) {
    const params = new URLSearchParams();
    params.set('scope', scope);
    if (q)
        params.set('q', q);
    const response = await apiFetch(`${BASE}?${params.toString()}`);
    return response.tags;
}
/**
 * Create an org tag. The backend dedupes on the normalized label and answers
 * 409 with `{ id, message }` carrying the existing row's id — callers treat
 * that exactly like a create (the id resolves either way), so it's folded
 * into the success path here.
 */
export async function createReportTag(request) {
    try {
        const created = await apiRequest('POST', BASE, request);
        return { id: created.id };
    }
    catch (e) {
        if (e instanceof ApiError && e.status === 409) {
            try {
                const body = JSON.parse(e.message);
                if (typeof body.id === 'string')
                    return { id: body.id };
            }
            catch {
                // 409 without a parseable body — surface the original error below.
            }
        }
        throw e;
    }
}
/** Rename an org tag / edit its aliases (v1 surface; omitted field = leave
 * unchanged; admin-gated server-side). Returns the updated tag. */
export async function updateReportTag(id, data) {
    return await apiRequest('PUT', `${BASE}/${encodeURIComponent(id)}`, data);
}
/** Archive an org tag (sets archived_at; admin-gated server-side). */
export async function archiveReportTag(id) {
    await apiRequest('DELETE', `${BASE}/${encodeURIComponent(id)}`);
}
function linksPath(type, reportId) {
    return `/reports/${type}/${encodeURIComponent(reportId)}/tags`;
}
/** Links (with tag info) for a report's chain — the server resolves to the
 * root and unions links across root + addenda. */
export async function getReportTagLinks(type, reportId) {
    const response = await apiFetch(linksPath(type, reportId));
    return response.links;
}
/**
 * Replace THIS report row's full tag-link set. Same edit ladder as the report
 * update endpoints; 400 once the report is final/amended. Returns the row's
 * canonical set — newTag entries come back resolved to their minted tag ids.
 */
export async function putReportTagLinks(type, reportId, links) {
    const response = await apiRequest('PUT', linksPath(type, reportId), {
        links,
    });
    return response.links;
}
